// The docked side panel (one persistent slot showing the single current item;
// never cleared by pointer-leave, only explicitly — Esc, the 👁 toggle,
// navigation) AND the owner of the keyboard-navigable active cell: `curCell` +
// its `.focus` outline, which mouse hover-follow, touch tap-to-anchor and
// arrow keys all move — panel open or closed, since the cursor is decoupled
// from the panel slot (built only
// while the panel is open). Exposes current()/focusFirstVisible()/navigate()
// to app.js's keydown, and fires onCurrent on every change so grid hover-play
// (panel closed) follows the same cell. The panel never shows or changes
// selection (a single tap is inert, reserved for future tap-to-play);
// double-tap opens the viewer; touch swipes live-track the finger and
// step/spring-back on release. Also owns --panel-w (persisted panelFrac) and
// the grabbable #panelDivider, and upgrades the cached thumb to the full-res
// original on a short debounce (PANEL_IMAGE_UPGRADE_DELAY).
import { cachedThumb } from './thumbs.js';
import { tapDist, verticalNeighbor } from './util.js';
import { settings } from './settings.js';
import { createSwipeTracker, runTransition, createTapDetector } from './gestures.js';
import { mountVideoPreview } from './video.js';

const PANEL_FRAC_MIN = 0.15, PANEL_FRAC_MAX = 0.75, PANEL_FRAC_DEFAULT = 0.4;
const PANEL_IMAGE_UPGRADE_DELAY = 150;

export function initPreview({ state, grid, viewer, openViewer, settingsPanel, onCurrent }) {
  const panelEl = document.getElementById('panel');
  const panelBody = document.getElementById('panelBody');
  const panelDivider = document.getElementById('panelDivider');
  const previewBtn = document.getElementById('previewBtn');

  let curCell = null;
  let previewSticky = false;



  function applyPanelW(frac) { document.documentElement.style.setProperty('--panel-w', frac * 100 + 'vw'); }
  applyPanelW(settings.get('panelFrac'));
  settings.onChange('panelFrac', applyPanelW);

  if (settingsPanel) {
    const rows = settingsPanel.addSection('Preview');
    settingsPanel.addSelect(rows, {
      label: 'Panel side', key: 'panelSide',
      options: [['right', 'Right'], ['left', 'Left']],
    });

    const vidRows = settingsPanel.addSection('Video preview');
    for (const spec of [
      { label: 'Segments', key: 'vidSegments', min: 1, max: 20, step: 1 },
      { label: 'Seconds per segment', key: 'vidSegSeconds', min: 0.5, max: 10, step: 0.5 },
      { label: 'Speed', key: 'vidRate', min: 0.5, max: 8, step: 0.5, suffix: '×' },
      { label: 'Grid hover delay (ms)', key: 'hoverDelay', min: 0, max: 3000, step: 50 },
    ]) settingsPanel.addNumber(vidRows, spec);
  }

  function applySide() {
    const left = settings.get('panelSide') === 'left';
    panelEl.classList.toggle('left', left);
    panelEl.classList.toggle('right', !left);
  }

  function setActive(active) {
    panelEl.classList.toggle('show', active);
    if (active) applySide();
  }

  async function setPanelImage(item, img, forCell) {
    try { img.src = await cachedThumb(item); } catch {}
    await new Promise(r => setTimeout(r, PANEL_IMAGE_UPGRADE_DELAY));
    if (curCell !== forCell) return null;
    let url;
    try {
      const file = await item.handle.getFile();
      if (curCell !== forCell) return null;
      url = URL.createObjectURL(file);
    } catch { return null; }
    if (curCell !== forCell) { URL.revokeObjectURL(url); return null; }
    img.src = url;
    return url;
  }

  // Build the side-panel content for `cell`. Separate from the `.focus` cursor
  // outline (which setCurrent owns) so the outline can show with the panel
  // closed and the slot is built only when the panel is open.
  function buildSlot(cell) {
    const slot = document.createElement('div');
    slot.className = 'pslot cur';
    slot._cell = cell;
    if (cell._item.kind === 'video') {
      const v = document.createElement('video');
      v.muted = true; v.loop = true; v.playsInline = true;
      slot.appendChild(v);
      const teardown = mountVideoPreview(slot, v, cell._item, {
        stillWanted: () => curCell === cell && slot.isConnected
      });
      teardown.then(t => { if (t) slot._cleanup = t; });
    } else {
      const img = document.createElement('img');
      slot.appendChild(img);
      setPanelImage(cell._item, img, cell).then(url => { if (url) slot._origUrl = url; });
    }
    panelBody.appendChild(slot);
  }

  // Tear down the panel slot(s) only — leaves the `.focus` cursor in place.
  function clearSlots() {
    for (const slot of panelBody.children) {
      if (slot._cleanup) slot._cleanup();
      const v = slot.querySelector('video');
      if (v) { try { v.pause(); } catch {} }
      if (slot._origUrl) URL.revokeObjectURL(slot._origUrl);
    }
    panelBody.replaceChildren();
    panelBody.style.transform = '';
  }
  const clearFocus = () => { for (const c of grid.querySelectorAll('.focus')) c.classList.remove('focus'); };

  // Full clear: cursor + panel. Exported as `clear` (render/tree call it).
  function clearPreview() {
    clearSlots();
    clearFocus();
    curCell = null;
    onCurrent?.(null);   // drop any grid hover-play targeting the cleared cell
  }

  function setCurrent(cell) {
    if (cell === curCell) return;
    clearSlots();
    clearFocus();
    curCell = cell;
    if (cell) {
      cell.classList.add('focus');         // cursor outline — always, even with the panel closed
      if (state.preview) buildSlot(cell);  // panel content — only when the panel is open
    }
    // The active cell drives grid hover-play (when the panel is closed) so
    // mouse/keyboard/touch all play the focused video the same way.
    onCurrent?.(cell);
  }

  const scrollCellIntoView = (cell) => cell?.scrollIntoView({ block: 'nearest', inline: 'nearest' });

  // First cell intersecting the grid's scroll viewport — the seed the first
  // keyboard nav press lands on when nothing is active yet.
  function firstVisibleCell() {
    const gr = grid.getBoundingClientRect();
    for (const c of grid.children) {
      const r = c.getBoundingClientRect();
      if (r.bottom > gr.top + 1 && r.top < gr.bottom - 1) return c;
    }
    return grid.firstElementChild;
  }
  function focusFirstVisible() {
    const c = firstVisibleCell();
    if (c) { setCurrent(c); scrollCellIntoView(c); }
    return c;
  }

  // The cell reached from `cell` in `dir`. Left/Right = DOM-order siblings (both
  // layout modes); Up/Down = index ± cols in grid mode, geometric row-neighbor
  // in justified-rows mode.
  function neighbor(cell, dir) {
    const kids = [...grid.children];
    const i = kids.indexOf(cell);
    if (i < 0) return null;
    if (dir === 'left') return kids[i - 1] || null;
    if (dir === 'right') return kids[i + 1] || null;
    if (state.displayMode === 'rows') return verticalNeighbor(cell, kids, dir);
    const j = i + (dir === 'up' ? -state.cols : state.cols);
    return (j >= 0 && j < kids.length) ? kids[j] : null;
  }

  // Keyboard-move the active cell in `dir`, scrolling it into view. With no
  // active cell, the first press just reveals it at the first visible cell.
  function navigate(dir) {
    if (!grid.children.length) return null;
    if (!curCell || !grid.contains(curCell)) return focusFirstVisible();
    const t = neighbor(curCell, dir);
    if (t) { setCurrent(t); scrollCellIntoView(t); }
    return curCell;
  }

  let lastMouse = null;
  function mouseMoved(e) {
    const moved = !lastMouse || lastMouse.x !== e.clientX || lastMouse.y !== e.clientY;
    lastMouse = { x: e.clientX, y: e.clientY };
    return moved;
  }

  function applyPreviewToggle() {
    previewBtn.classList.toggle('active', state.preview);
    setActive(state.preview);
    // The active-cell cursor survives the toggle; only the panel slot follows
    // state.preview — built when the panel opens for the current cell, torn
    // down when it closes (the `.focus` outline stays for keyboard nav).
    if (state.preview) { if (curCell && !panelBody.children.length) buildSlot(curCell); }
    else { clearSlots(); onCurrent?.(curCell); }   // closing hands the focused video back to grid hover-play
  }
  function togglePreview() {
    state.preview = !state.preview;
    settings.set('preview', state.preview);
    applyPreviewToggle();
  }
  previewBtn.onclick = togglePreview;

  settings.onChange('panelSide', () => { if (state.preview) applySide(); });

  grid.addEventListener('contextmenu', (e) => e.preventDefault());
  panelEl.addEventListener('contextmenu', (e) => e.preventDefault());

  // Mouse hover moves the active cell in BOTH modes — the `.focus` cursor and
  // (panel closed) grid hover-play follow the pointer, matching keyboard/touch.
  grid.addEventListener('pointermove', (e) => {
    if (e.pointerType !== 'mouse' || !mouseMoved(e)) return;
    if (document.body.classList.contains('dragging')) return;
    setCurrent(e.target.closest('.cell'));
  });

  grid.addEventListener('pointerup', (e) => {
    if (!viewer.hidden || document.body.classList.contains('dragging')) return;
    // A long-press range-select lift is not a preview tap — don't re-anchor.
    if (document.body.classList.contains('range-selecting')) return;
    if (e.pointerType !== 'touch') return;
    if (tapDist(e) > 30) return;
    const cell = e.target.closest('.cell');
    if (cell) setCurrent(cell);
  });

  function animateStep(sign) {
    const nb = sign > 0 ? curCell?.previousElementSibling : curCell?.nextElementSibling;
    if (!nb || !nb.classList.contains('cell')) { springBack(); return; }
    const w = panelBody.clientWidth || 1;
    runTransition(panelBody, 'transform .15s ease', () => {
      panelBody.style.transform = `translateX(${sign * w}px)`;
    }, () => {
      panelBody.style.transition = 'none';
      panelBody.style.transform = '';
      setCurrent(nb);
    });
  }

  function springBack() {
    runTransition(panelBody, 'transform .15s ease', () => {
      panelBody.style.transform = '';
    }, () => {
      panelBody.style.transition = 'none';
    });
  }

  const detectTap = createTapDetector();
  function resolveTap(e) {
    if (tapDist(e) > 30) return;
    const cell = curCell;
    if (!cell) return;
    // Selection lives only on the grid — a single tap on the panel is inert
    // (a future tap-to-play-video feature lands here). Double-tap opens the
    // viewer.
    if (detectTap(e, cell) === 'double') openViewer(cell._item);
  }

  let track = null, dragged = false;
  function beginSwipe(e) {
    track = createSwipeTracker();
    track.start(e.clientX, e.clientY);
    dragged = false;
    panelBody.style.transition = 'none';
    try { panelBody.setPointerCapture(e.pointerId); } catch {}
  }
  panelBody.addEventListener('pointerdown', (e) => {
    if (!state.preview || e.pointerType !== 'touch') return;
    beginSwipe(e);
  });
  panelBody.addEventListener('pointermove', (e) => {
    if (!state.preview || e.pointerType !== 'touch' || !track) return;
    const { dx, dy } = track.move(e.clientX, e.clientY);
    if (!dragged && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) dragged = true;
    if (dragged) panelBody.style.transform = `translateX(${dx}px)`;
  });
  panelBody.addEventListener('pointercancel', () => {
    track = null;
    if (dragged) springBack();
    dragged = false;
  });
  panelBody.addEventListener('pointerup', (e) => {
    if (!state.preview) return;
    e.stopPropagation();
    if (e.pointerType !== 'touch') return;
    const t = track, d = dragged;
    track = null; dragged = false;
    if (t && d) {
      const r = t.end(e.clientX, e.clientY);
      if (r.commit && r.axis === 'x') animateStep(r.sign);
      else springBack();
      return;
    }
    resolveTap(e);
  });

  let dividerDragging = false, dividerPtrId = null;
  panelDivider.addEventListener('pointerdown', (e) => {
    dividerDragging = true; dividerPtrId = e.pointerId;
    panelDivider.classList.add('dragging');
    try { panelDivider.setPointerCapture(e.pointerId); } catch {}
    e.stopPropagation();
  });
  panelDivider.addEventListener('pointermove', (e) => {
    if (!dividerDragging || e.pointerId !== dividerPtrId) return;
    const left = settings.get('panelSide') === 'left';
    // Measure from the panel's own stable edge, not the window edge — with the
    // directory tree open, a left-docked panel no longer starts at x=0. The
    // stable edge is the one not being dragged: left edge of a left panel
    // (pinned against the tree/window), right edge of a right panel.
    const r = panelEl.getBoundingClientRect();
    const w = left ? e.clientX - r.left : r.right - e.clientX;
    const frac = Math.min(PANEL_FRAC_MAX, Math.max(PANEL_FRAC_MIN, w / innerWidth));
    applyPanelW(frac);
  });
  function endDivider(e) {
    if (!dividerDragging || e.pointerId !== dividerPtrId) return;
    dividerDragging = false;
    panelDivider.classList.remove('dragging');
    const cur = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--panel-w')) / 100;
    settings.set('panelFrac', cur);
  }
  panelDivider.addEventListener('pointerup', endDivider);
  panelDivider.addEventListener('pointercancel', endDivider);
  panelDivider.addEventListener('dblclick', () => {
    applyPanelW(PANEL_FRAC_DEFAULT);
    settings.set('panelFrac', PANEL_FRAC_DEFAULT);
  });

  applyPreviewToggle();

  return { setCurrent, clear: clearPreview, togglePreview, current: () => curCell, focusFirstVisible, navigate };
}
